<template>
  <Transition name="slide-fade">
    <div v-if="visible" class="border-b border-gray-100 px-4 py-2.5 bg-primary/10" data-testid="create-session-form">
      <form class="grid grid-cols-1 md:grid-cols-4 gap-2 items-end" @submit.prevent="handleCreate">
        <div class="relative" ref="dropdownRef">
          <label class="text-xs font-medium text-gray-600 block mb-0.5">Session Name</label>
          <InputText
            v-model="searchQuery"
            placeholder="e.g. 2024/2025"
            class="w-full"
            @focus="showDropdown = true"
            @input="showDropdown = true"
            data-testid="session-name-input"
          />
          <Transition name="fade">
            <ul v-if="showDropdown && filteredNames.length"
              class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
              <li v-for="name in filteredNames" :key="name"
                class="px-2.5 py-1.5 text-xs cursor-pointer hover:bg-primary/5 hover:text-primary"
                @mousedown.prevent="selectName(name)">
                {{ name }}
              </li>
            </ul>
          </Transition>
          <small v-if="errors.name" class="text-red-500 text-xs">{{ errors.name }}</small>
        </div>
        <div>
          <label class="text-xs font-medium text-gray-600 block mb-0.5">Start Date</label>
          <Calendar v-model="form.started_at" dateFormat="yy-mm-dd" class="w-full" showIcon data-testid="session-start-date" />
          <small v-if="errors.started_at" class="text-red-500 text-xs">{{ errors.started_at }}</small>
        </div>
        <div>
          <label class="text-xs font-medium text-gray-600 block mb-0.5">End Date</label>
          <Calendar v-model="form.ended_at" dateFormat="yy-mm-dd" class="w-full" showIcon data-testid="session-end-date" />
          <small v-if="errors.ended_at" class="text-red-500 text-xs">{{ errors.ended_at }}</small>
        </div>
        <Button type="submit" label="Create Session" icon="pi pi-check" size="small" :loading="isCreating" data-testid="btn-create-session" />
      </form>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import { generateSessions } from '@/utils';
import { QUERY_ALL_SESSION_STRUCTURES } from '@/graphql';

import { extractGraphQLError } from '@/utils/graphqlError';
defineProps<{ visible: boolean }>();
const emit = defineEmits<{ created: [] }>();
const toast = useToast();
const { createSession } = useSchoolMutations();

const isCreating = ref(false);
const searchQuery = ref('');
const showDropdown = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);
const errors = ref<Record<string, string>>({});
const allNames = ref(generateSessions(-3, 10));

const filteredNames = computed(() =>
  allNames.value.filter(s => s.toLowerCase().includes(searchQuery.value.toLowerCase())),
);

const form = ref({
  name: '',
  started_at: undefined as Date | undefined,
  ended_at: undefined as Date | undefined,
});

const { result: sessionStructureResult } = QUERY_ALL_SESSION_STRUCTURES();
const sessionStructureId = ref('');

watch(sessionStructureResult, (res) => {
  if (res?.allSessionStructures?.length) {
    sessionStructureId.value = res.allSessionStructures[res.allSessionStructures.length - 1].id;
  }
});

function selectName(name: string) {
  form.value.name = name;
  searchQuery.value = name;
  showDropdown.value = false;
}

function closeDropdown(e: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    showDropdown.value = false;
  }
}

onMounted(() => document.addEventListener('click', closeDropdown));
onUnmounted(() => document.removeEventListener('click', closeDropdown));

async function handleCreate() {
  errors.value = {};
  if (!form.value.name && !searchQuery.value) errors.value.name = 'Session name is required.';
  if (!form.value.started_at) errors.value.started_at = 'Start date is required.';
  if (!form.value.ended_at) errors.value.ended_at = 'End date is required.';
  if (form.value.started_at && form.value.ended_at) {
    if (new Date(form.value.started_at).getTime() >= new Date(form.value.ended_at).getTime()) {
      errors.value.ended_at = 'End date must be after start date.';
    }
  }
  if (Object.keys(errors.value).length) return;

  if (!sessionStructureId.value) {
    toast.add({ severity: 'error', summary: 'No Session Structure', detail: 'Create a session structure before creating a session.', life: 5000 });
    return;
  }

  isCreating.value = true;
  try {
    await createSession({
      name: form.value.name || searchQuery.value,
      started_at: form.value.started_at ? new Date(form.value.started_at).toISOString() : null,
      ended_at: form.value.ended_at ? new Date(form.value.ended_at).toISOString() : null,
      session_structure_id: sessionStructureId.value,
    });
    form.value = { name: '', started_at: undefined, ended_at: undefined };
    searchQuery.value = '';
    toast.add({ severity: 'success', summary: 'Session Created', detail: 'Session created successfully', life: 3000 });
    emit('created');
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Failed to create session'), life: 3000 });
  } finally {
    isCreating.value = false;
  }
}
</script>
